// Primary 5 English Learning Data - Part 2 (Topics 11-18)
// Based on Primary English Learning P5 Topics.txt

export const p5DataPart2 = {
  topics: [
    {
      id: 'p5-topic-11',
      title: 'Advanced Vocabulary',
      description: 'Learning to use advanced vocabulary and expressions',
      purpose: 'To teach students how to use advanced vocabulary, synonyms, antonyms, and sophisticated expressions to improve their language skills.',
      questions: [
        {
          question: 'What is a synonym for "happy"?',
          answer: 'joyful',
          explanation: '"Joyful" is a synonym for "happy" - both mean feeling pleasure or contentment.'
        },
        {
          question: 'What is an antonym for "big"?',
          answer: 'small',
          explanation: '"Small" is an antonym for "big" - they have opposite meanings.'
        },
        {
          question: 'What is a synonym for "beautiful"?',
          answer: 'gorgeous',
          explanation: '"Gorgeous" is a synonym for "beautiful" - both mean very attractive.'
        },
        {
          question: 'What is an antonym for "fast"?',
          answer: 'slow',
          explanation: '"Slow" is an antonym for "fast" - they have opposite meanings.'
        },
        {
          question: 'What is a synonym for "smart"?',
          answer: 'intelligent',
          explanation: '"Intelligent" is a synonym for "smart" - both mean having good mental ability.'
        },
        {
          question: 'What is an antonym for "hot"?',
          answer: 'cold',
          explanation: '"Cold" is an antonym for "hot" - they have opposite meanings.'
        },
        {
          question: 'What is a synonym for "tired"?',
          answer: 'exhausted',
          explanation: '"Exhausted" is a synonym for "tired" - both mean feeling weary.'
        },
        {
          question: 'What is an antonym for "rich"?',
          answer: 'poor',
          explanation: '"Poor" is an antonym for "rich" - they have opposite meanings.'
        },
        {
          question: 'What is a synonym for "angry"?',
          answer: 'furious',
          explanation: '"Furious" is a synonym for "angry" - both mean very mad.'
        },
        {
          question: 'What is an antonym for "young"?',
          answer: 'old',
          explanation: '"Old" is an antonym for "young" - they have opposite meanings.'
        }
      ]
    },
    {
      id: 'p5-topic-12',
      title: 'Idioms and Expressions',
      description: 'Learning to understand and use advanced idioms and expressions',
      purpose: 'To teach students advanced idioms and expressions that are commonly used in English to improve their comprehension and expression.',
      questions: [
        {
          question: 'What does "hit the nail on the head" mean?',
          answer: 'be exactly right',
          explanation: '"Hit the nail on the head" means to be exactly right or accurate about something.'
        },
        {
          question: 'What does "break the ice" mean?',
          answer: 'start a conversation',
          explanation: '"Break the ice" means to start a conversation in a social situation.'
        },
        {
          question: 'What does "barking up the wrong tree" mean?',
          answer: 'looking in the wrong place',
          explanation: '"Barking up the wrong tree" means to look in the wrong place for something.'
        },
        {
          question: 'What does "get out of hand" mean?',
          answer: 'become uncontrollable',
          explanation: '"Get out of hand" means to become uncontrollable or chaotic.'
        },
        {
          question: 'What does "pull someone\'s leg" mean?',
          answer: 'joke with someone',
          explanation: '"Pull someone\'s leg" means to joke with someone or tease them.'
        },
        {
          question: 'What does "let the cat out of the bag" mean?',
          answer: 'reveal a secret',
          explanation: '"Let the cat out of the bag" means to reveal a secret or surprise.'
        },
        {
          question: 'What does "cost an arm and a leg" mean?',
          answer: 'very expensive',
          explanation: '"Cost an arm and a leg" means something is very expensive.'
        },
        {
          question: 'What does "hit the books" mean?',
          answer: 'study hard',
          explanation: '"Hit the books" means to study hard or intensively.'
        },
        {
          question: 'What does "piece of cake" mean?',
          answer: 'very easy',
          explanation: '"Piece of cake" means something is very easy to do.'
        },
        {
          question: 'What does "break a leg" mean?',
          answer: 'good luck',
          explanation: '"Break a leg" is an idiom meaning "good luck", often used before performances.'
        }
      ]
    },
    {
      id: 'p5-topic-13',
      title: 'Advanced Prepositions',
      description: 'Learning to use advanced prepositions and prepositional phrases',
      purpose: 'To teach students how to use advanced prepositions and prepositional phrases in complex contexts.',
      questions: [
        {
          question: 'I am responsible _____ the project.',
          answer: 'for',
          explanation: 'Use "for" after "responsible" to show what someone is responsible for.'
        },
        {
          question: 'She is good _____ playing piano.',
          answer: 'at',
          explanation: 'Use "at" after "good" to show skill or ability.'
        },
        {
          question: 'He is interested _____ science.',
          answer: 'in',
          explanation: 'Use "in" after "interested" to show what someone is interested in.'
        },
        {
          question: 'They are proud _____ their son.',
          answer: 'of',
          explanation: 'Use "of" after "proud" to show what someone is proud of.'
        },
        {
          question: 'I am afraid _____ spiders.',
          answer: 'of',
          explanation: 'Use "of" after "afraid" to show what someone is afraid of.'
        },
        {
          question: 'She is famous _____ her singing.',
          answer: 'for',
          explanation: 'Use "for" after "famous" to show what someone is famous for.'
        },
        {
          question: 'He is bad _____ math.',
          answer: 'at',
          explanation: 'Use "at" after "bad" to show lack of skill or ability.'
        },
        {
          question: 'They are excited _____ the trip.',
          answer: 'about',
          explanation: 'Use "about" after "excited" to show what someone is excited about.'
        },
        {
          question: 'I am tired _____ studying.',
          answer: 'of',
          explanation: 'Use "of" after "tired" to show what someone is tired of.'
        },
        {
          question: 'She is worried _____ her exam.',
          answer: 'about',
          explanation: 'Use "about" after "worried" to show what someone is worried about.'
        }
      ]
    },
    {
      id: 'p5-topic-14',
      title: 'Advanced Conjunctions',
      description: 'Learning to use advanced conjunctions and linking words',
      purpose: 'To teach students how to use advanced conjunctions and linking words to create complex sentences and show relationships between ideas.',
      questions: [
        {
          question: 'Complete: "I will go to the party _____ you come with me."',
          answer: 'provided that',
          explanation: 'Use "provided that" to introduce a condition.'
        },
        {
          question: 'Fill in the blank: "_____ it rains, I will stay home."',
          answer: 'In case',
          explanation: 'Use "In case" to introduce a possible situation.'
        },
        {
          question: 'Complete: "I like both tea _____ coffee."',
          answer: 'and',
          explanation: 'Use "and" to connect two similar ideas.'
        },
        {
          question: 'Fill in the blank: "_____ you study hard, you will pass."',
          answer: 'As long as',
          explanation: 'Use "As long as" to introduce a condition.'
        },
        {
          question: 'Complete: "I will help you _____ you need it."',
          answer: 'whenever',
          explanation: 'Use "whenever" to mean "at any time that".'
        },
        {
          question: 'Fill in the blank: "_____ I was tired, I continued working."',
          answer: 'Although',
          explanation: 'Use "Although" to show contrast or concession.'
        },
        {
          question: 'Complete: "I will call you _____ I arrive."',
          answer: 'as soon as',
          explanation: 'Use "as soon as" to mean "immediately when".'
        },
        {
          question: 'Fill in the blank: "_____ you finish your homework, you can play."',
          answer: 'Once',
          explanation: 'Use "Once" to mean "when" or "after".'
        },
        {
          question: 'Complete: "I will wait _____ you are ready."',
          answer: 'until',
          explanation: 'Use "until" to show the time when something will happen.'
        },
        {
          question: 'Fill in the blank: "_____ it was late, we continued talking."',
          answer: 'Even though',
          explanation: 'Use "Even though" to show contrast or concession.'
        }
      ]
    },
    {
      id: 'p5-topic-15',
      title: 'Advanced Articles',
      description: 'Learning to use articles correctly in complex contexts',
      purpose: 'To teach students how to use definite and indefinite articles correctly in complex sentences and contexts.',
      questions: [
        {
          question: '_____ sun rises in the east.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I have _____ apple and _____ orange.',
          answer: 'an, an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ book on the table is mine.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific book.'
        },
        {
          question: 'She is _____ teacher.',
          answer: 'a',
          explanation: 'Use "a" before words that begin with consonant sounds.'
        },
        {
          question: '_____ earth orbits around the sun.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I need _____ umbrella because it\'s raining.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ car in the driveway is red.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific car.'
        },
        {
          question: 'He is _____ engineer.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ moon is beautiful tonight.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I want _____ banana and _____ apple.',
          answer: 'a, an',
          explanation: 'Use "a" before consonant sounds and "an" before vowel sounds.'
        }
      ]
    },
    {
      id: 'p5-topic-16',
      title: 'Advanced Tenses',
      description: 'Learning to use advanced tense combinations and perfect forms',
      purpose: 'To teach students how to use advanced tense combinations and perfect forms to express complex time relationships.',
      questions: [
        {
          question: 'I _____ (study) English for five years by next month.',
          answer: 'will have been studying',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'She _____ (work) here for ten years before she retires.',
          answer: 'will have worked',
          explanation: 'Use "will have + past participle" for future perfect.'
        },
        {
          question: 'They _____ (live) in Hong Kong for three years when they move.',
          answer: 'will have been living',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'He _____ (finish) the project by Friday.',
          answer: 'will have finished',
          explanation: 'Use "will have + past participle" for future perfect.'
        },
        {
          question: 'I _____ (wait) for two hours when you arrive.',
          answer: 'will have been waiting',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'She _____ (cook) dinner for three hours by 8 PM.',
          answer: 'will have been cooking',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'They _____ (build) the house for six months by December.',
          answer: 'will have been building',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'He _____ (practice) piano for five years by next year.',
          answer: 'will have been practicing',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'We _____ (travel) for two weeks by the time we return.',
          answer: 'will have been traveling',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        },
        {
          question: 'I _____ (read) this book for a month by next week.',
          answer: 'will have been reading',
          explanation: 'Use "will have been + verb-ing" for future perfect continuous.'
        }
      ]
    },
    {
      id: 'p5-topic-17',
      title: 'Advanced Sentence Patterns',
      description: 'Learning to construct advanced sentence patterns and structures',
      purpose: 'To teach students how to construct advanced sentence patterns including inverted sentences, cleft sentences, and complex structures.',
      questions: [
        {
          question: 'Complete: "Not only _____ she speak English, but she also speaks French."',
          answer: 'does',
          explanation: 'Use inverted word order after "Not only" for emphasis.'
        },
        {
          question: 'Fill in the blank: "_____ did I see the movie, but I also read the book."',
          answer: 'Not only',
          explanation: 'Use "Not only" to emphasize that something is true and something else is also true.'
        },
        {
          question: 'Complete: "It was John _____ helped me."',
          answer: 'who',
          explanation: 'Use "who" in a cleft sentence to emphasize the subject.'
        },
        {
          question: 'Fill in the blank: "_____ was the food delicious, but the service was excellent."',
          answer: 'Not only',
          explanation: 'Use "Not only" to emphasize that something is true and something else is also true.'
        },
        {
          question: 'Complete: "It was in 2020 _____ I graduated."',
          answer: 'that',
          explanation: 'Use "that" in a cleft sentence to emphasize the time.'
        },
        {
          question: 'Fill in the blank: "_____ did he arrive late, but he also forgot his homework."',
          answer: 'Not only',
          explanation: 'Use "Not only" to emphasize that something is true and something else is also true.'
        },
        {
          question: 'Complete: "It was the book _____ I wanted."',
          answer: 'that',
          explanation: 'Use "that" in a cleft sentence to emphasize the object.'
        },
        {
          question: 'Fill in the blank: "_____ did she pass the exam, but she got the highest score."',
          answer: 'Not only',
          explanation: 'Use "Not only" to emphasize that something is true and something else is also true.'
        },
        {
          question: 'Complete: "It was at the library _____ I found the book."',
          answer: 'where',
          explanation: 'Use "where" in a cleft sentence to emphasize the place.'
        },
        {
          question: 'Fill in the blank: "_____ did they win the game, but they also broke a record."',
          answer: 'Not only',
          explanation: 'Use "Not only" to emphasize that something is true and something else is also true.'
        }
      ]
    },
    {
      id: 'p5-topic-18',
      title: 'Writing Techniques',
      description: 'Learning advanced writing techniques and styles',
      purpose: 'To teach students advanced writing techniques including descriptive writing, persuasive writing, and narrative writing with sophisticated language.',
      questions: [
        {
          question: 'What is the purpose of descriptive writing?',
          answer: 'to create vivid images',
          explanation: 'Descriptive writing aims to create vivid images in the reader\'s mind using sensory details.'
        },
        {
          question: 'What is the purpose of persuasive writing?',
          answer: 'to convince the reader',
          explanation: 'Persuasive writing aims to convince the reader to agree with the writer\'s point of view.'
        },
        {
          question: 'What is the purpose of narrative writing?',
          answer: 'to tell a story',
          explanation: 'Narrative writing aims to tell a story with characters, plot, and setting.'
        },
        {
          question: 'What is a topic sentence?',
          answer: 'the main idea of a paragraph',
          explanation: 'A topic sentence states the main idea of a paragraph and controls what the paragraph will be about.'
        },
        {
          question: 'What is a concluding sentence?',
          answer: 'summarizes the paragraph',
          explanation: 'A concluding sentence summarizes the main points of a paragraph and brings it to a close.'
        },
        {
          question: 'What is the purpose of a thesis statement?',
          answer: 'to state the main argument',
          explanation: 'A thesis statement states the main argument or point of an essay.'
        },
        {
          question: 'What is the purpose of supporting details?',
          answer: 'to provide evidence',
          explanation: 'Supporting details provide evidence, examples, or explanations to support the main idea.'
        },
        {
          question: 'What is the purpose of transitions?',
          answer: 'to connect ideas',
          explanation: 'Transitions connect ideas and help the writing flow smoothly from one point to the next.'
        },
        {
          question: 'What is the purpose of a hook?',
          answer: 'to grab the reader\'s attention',
          explanation: 'A hook is an opening sentence designed to grab the reader\'s attention and make them want to continue reading.'
        },
        {
          question: 'What is the purpose of a conclusion?',
          answer: 'to wrap up the essay',
          explanation: 'A conclusion wraps up the essay by summarizing the main points and restating the thesis.'
        }
      ]
    }
  ]
}; 